import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "react-toastify";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const MinisterDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get active tab from URL query params or default to "dashboard"
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get("tab") || "dashboard";

  const [complaints, setComplaints] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [activeFilter, setActiveFilter] = useState(null);
  const [error, setError] = useState(null);

  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000";

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");

        if (!token) {
          setError("No authentication token found");
          setLoading(false);
          return;
        }

        const complaintsRes = await axios.get(`${API_URL}/api/complaints`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const suggestionsRes = await axios.get(`${API_URL}/api/suggestions`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setComplaints(complaintsRes.data?.data || []);
        setSuggestions(suggestionsRes.data?.data || []);
        setError(null);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError(err.response?.data?.message || "Failed to fetch data");
        setComplaints([]);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_URL]);

  // Update URL when tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setActiveFilter(null); // reset filter on tab change
    // Update URL query parameter without causing navigation
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set("tab", tabId);
    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  };

  // Calculate stats
  const pendingComplaints = complaints.filter(
    (c) => c.status === "pending",
  ).length;
  const pendingSuggestions = suggestions.filter(
    (s) => s.status === "pending",
  ).length;
  const totalPending = pendingComplaints + pendingSuggestions;

  const approvedComplaints = complaints.filter(
    (c) => c.status === "approved",
  ).length;
  const approvedSuggestions = suggestions.filter(
    (s) => s.status === "approved",
  ).length;
  const totalApproved = approvedComplaints + approvedSuggestions;

  const rejectedComplaints = complaints.filter(
    (c) => c.status === "rejected",
  ).length;
  const rejectedSuggestions = suggestions.filter(
    (s) => s.status === "rejected",
  ).length;
  const totalRejected = rejectedComplaints + rejectedSuggestions;

  const resolvedComplaints = complaints.filter(
    (c) => c.status === "resolved",
  ).length;
  const resolvedSuggestions = suggestions.filter(
    (s) => s.status === "resolved",
  ).length;
  const totalResolved = resolvedComplaints + resolvedSuggestions;

  // Get filtered items based on active filter
  const filteredComplaints =
    activeFilter === "total-complaints"
      ? complaints
      : activeFilter && activeFilter !== "total-suggestions"
        ? complaints.filter((c) => c.status?.toLowerCase() === activeFilter)
        : [];

  const filteredSuggestions =
    activeFilter === "total-suggestions"
      ? suggestions
      : activeFilter && activeFilter !== "total-complaints"
        ? suggestions.filter((s) => s.status?.toLowerCase() === activeFilter)
        : [];

  const getStatusBadge = (status) => {
    const colors = {
      pending: "#f39c12",
      approved: "#27ae60",
      rejected: "#e74c3c",
      resolved: "#2ecc71",
      "in-progress": "#3498db",
      "under-review": "#8e44ad",
      implemented: "#2ecc71",
    };
    return {
      background: colors[status?.toLowerCase()] || "#95a5a6",
      color: "white",
      padding: "4px 12px",
      borderRadius: "12px",
      fontSize: "12px",
      fontWeight: "600",
      textTransform: "capitalize",
    };
  };

  const handleItemClick = (id, type) => {
    // Store current tab and filter in sessionStorage before navigating
    sessionStorage.setItem("dashboardReturnTab", activeTab);
    if (activeFilter) {
      sessionStorage.setItem("dashboardReturnFilter", activeFilter);
    } else {
      sessionStorage.removeItem("dashboardReturnFilter");
    }

    if (type === "complaint") {
      navigate(`/complaint/${id}`);
    } else {
      navigate(`/suggestion/${id}`);
    }
  };

  const handleFilterClick = (filterKey) => {
    setActiveFilter((prev) => (prev === filterKey ? null : filterKey));
  };

  const getFilterTitle = () => {
    switch (activeFilter) {
      case "total-complaints":
        return "All Complaints";
      case "total-suggestions":
        return "All Suggestions";
      case "pending":
        return "Pending Items";
      case "approved":
        return "Approved Items";
      case "rejected":
        return "Rejected Items";
      case "resolved":
        return "Resolved Items";
      default:
        return "";
    }
  };

  // Check sessionStorage on component mount to restore tab and filter
  useEffect(() => {
    const returnTab = sessionStorage.getItem("dashboardReturnTab");
    const returnFilter = sessionStorage.getItem("dashboardReturnFilter");

    if (returnTab && returnTab !== activeTab) {
      setActiveTab(returnTab);

      // Update URL
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.set("tab", returnTab);
      navigate(`${location.pathname}?${newSearchParams.toString()}`, {
        replace: true,
      });
    }

    if (returnFilter) {
      setActiveFilter(returnFilter);

      // Scroll back to the filtered section after it renders
      setTimeout(() => {
        const el = document.querySelector(".filtered-section");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    }

    // Clear sessionStorage after restoring
    sessionStorage.removeItem("dashboardReturnTab");
    sessionStorage.removeItem("dashboardReturnFilter");
  }, []);

  // Tab configuration
  const tabs = [
    { id: "dashboard", label: "Dashboard" },
    { id: "complaints", label: `Complaints (${complaints.length})` },
    { id: "suggestions", label: `Suggestions (${suggestions.length})` },
  ];

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="spinner-wrapper">
          <div className="spinner-container">
            <div className="spinner"></div>
            <p className="loading-text">Loading Dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-page">
        <Navbar />
        <div className="error-wrapper">
          <div className="error-container">
            <p className="error-text">Error: {error}</p>
            <button
              onClick={() => window.location.reload()}
              className="retry-btn"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="minister-dashboard">
      <div className="dashboard-layout">
        {/* Left Sidebar - Tabs */}
        <div className="dashboard-sidebar">
          <div className="sidebar-header">
            <h2>Minister Panel</h2>
            <p className="sidebar-user">Minister KP</p>
          </div>
          <nav className="sidebar-nav">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`sidebar-tab ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => handleTabChange(tab.id)}
              >
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </nav>
          <div className="sidebar-footer">
            <span className="role-badge">Minister</span>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="dashboard-content">
          {/* ========== DASHBOARD TAB ========== */}
          {activeTab === "dashboard" && (
            <>
              <div className="content-header">
                <h1>Dashboard</h1>
              </div>

              {/* All Stats Cards - Clickable */}
              <div className="stats-grid">
                <div
                  className={`stat-card ${activeFilter === "total-complaints" ? "active-filter" : ""}`}
                  onClick={() => handleFilterClick("total-complaints")}
                >
                  <div className="stat-number">{complaints.length}</div>
                  <div className="stat-label">Total Complaints</div>
                </div>

                <div
                  className={`stat-card ${activeFilter === "total-suggestions" ? "active-filter" : ""}`}
                  onClick={() => handleFilterClick("total-suggestions")}
                >
                  <div className="stat-number">{suggestions.length}</div>
                  <div className="stat-label">Total Suggestions</div>
                </div>

                <div
                  className={`stat-card pending-card ${activeFilter === "pending" ? "active-filter" : ""}`}
                  onClick={() => handleFilterClick("pending")}
                >
                  <div className="stat-number pending-number">
                    {totalPending}
                  </div>
                  <div className="stat-label">Pending</div>
                  <div className="stat-sub">
                    {pendingComplaints} C • {pendingSuggestions} S
                  </div>
                </div>

                <div
                  className={`stat-card approved-card ${activeFilter === "approved" ? "active-filter" : ""}`}
                  onClick={() => handleFilterClick("approved")}
                >
                  <div className="stat-number approved-number">
                    {totalApproved}
                  </div>
                  <div className="stat-label">Approved</div>
                  <div className="stat-sub">
                    {approvedComplaints} C • {approvedSuggestions} S
                  </div>
                </div>

                <div
                  className={`stat-card rejected-card ${activeFilter === "rejected" ? "active-filter" : ""}`}
                  onClick={() => handleFilterClick("rejected")}
                >
                  <div className="stat-number rejected-number">
                    {totalRejected}
                  </div>
                  <div className="stat-label">Rejected</div>
                  <div className="stat-sub">
                    {rejectedComplaints} C • {rejectedSuggestions} S
                  </div>
                </div>

                <div
                  className={`stat-card resolved-card ${activeFilter === "resolved" ? "active-filter" : ""}`}
                  onClick={() => handleFilterClick("resolved")}
                >
                  <div className="stat-number resolved-number">
                    {totalResolved}
                  </div>
                  <div className="stat-label">Resolved</div>
                  <div className="stat-sub">
                    {resolvedComplaints} C • {resolvedSuggestions} S
                  </div>
                </div>
              </div>

              {/* Filtered Results Section */}
              {activeFilter && (
                <div className="filtered-section">
                  <div className="filtered-header">
                    <h3 className="card-title">{getFilterTitle()}</h3>
                    <button
                      className="clear-filter-btn"
                      onClick={() => setActiveFilter(null)}
                    >
                      ✕ Clear Filter
                    </button>
                  </div>

                  {activeFilter === "total-complaints" ? (
                    complaints.length === 0 ? (
                      <p className="empty-assigned">No complaints found</p>
                    ) : (
                      <div className="table-wrapper">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Tracking ID</th>
                              <th>Name</th>
                              <th>District</th>
                              <th>Category</th>
                              <th>Subject</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {complaints.map((item, i) => (
                              <tr
                                key={item._id}
                                onClick={() =>
                                  handleItemClick(item._id, "complaint")
                                }
                              >
                                <td>{i + 1}</td>
                                <td>
                                  <span className="tracking-id">
                                    {item.trackingId}
                                  </span>
                                </td>
                                <td className="name-cell">
                                  {item.citizenName}
                                </td>
                                <td>{item.district}</td>
                                <td>{item.category}</td>
                                <td>{item.subject}</td>
                                <td>
                                  <span
                                    className="status-badge"
                                    style={getStatusBadge(item.status)}
                                  >
                                    {item.status}
                                  </span>
                                </td>
                                <td>
                                  <button
                                    className="view-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleItemClick(item._id, "complaint");
                                    }}
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  ) : activeFilter === "total-suggestions" ? (
                    suggestions.length === 0 ? (
                      <p className="empty-assigned">No suggestions found</p>
                    ) : (
                      <div className="table-wrapper">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Tracking ID</th>
                              <th>Name</th>
                              <th>District</th>
                              <th>Category</th>
                              <th>Subject</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {suggestions.map((item, i) => (
                              <tr
                                key={item._id}
                                onClick={() =>
                                  handleItemClick(item._id, "suggestion")
                                }
                              >
                                <td>{i + 1}</td>
                                <td>
                                  <span className="tracking-id">
                                    {item.trackingId}
                                  </span>
                                </td>
                                <td className="name-cell">
                                  {item.citizenName}
                                </td>
                                <td>{item.district}</td>
                                <td>{item.category}</td>
                                <td>{item.subject}</td>
                                <td>
                                  <span
                                    className="status-badge"
                                    style={getStatusBadge(item.status)}
                                  >
                                    {item.status}
                                  </span>
                                </td>
                                <td>
                                  <button
                                    className="view-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleItemClick(item._id, "suggestion");
                                    }}
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  ) : filteredComplaints.length === 0 &&
                    filteredSuggestions.length === 0 ? (
                    <p className="empty-assigned">
                      No items with status "{activeFilter}"
                    </p>
                  ) : (
                    <>
                      {filteredComplaints.length > 0 && (
                        <>
                          <h4 className="filtered-subtitle">
                            Complaints ({filteredComplaints.length})
                          </h4>
                          <div className="table-wrapper">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>Tracking ID</th>
                                  <th>Name</th>
                                  <th>District</th>
                                  <th>Category</th>
                                  <th>Subject</th>
                                  <th>Status</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredComplaints.map((item, i) => (
                                  <tr
                                    key={item._id}
                                    onClick={() =>
                                      handleItemClick(item._id, "complaint")
                                    }
                                  >
                                    <td>{i + 1}</td>
                                    <td>
                                      <span className="tracking-id">
                                        {item.trackingId}
                                      </span>
                                    </td>
                                    <td className="name-cell">
                                      {item.citizenName}
                                    </td>
                                    <td>{item.district}</td>
                                    <td>{item.category}</td>
                                    <td>{item.subject}</td>
                                    <td>
                                      <span
                                        className="status-badge"
                                        style={getStatusBadge(item.status)}
                                      >
                                        {item.status}
                                      </span>
                                    </td>
                                    <td>
                                      <button
                                        className="view-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleItemClick(
                                            item._id,
                                            "complaint",
                                          );
                                        }}
                                      >
                                        View
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}

                      {filteredSuggestions.length > 0 && (
                        <>
                          <h4
                            className="filtered-subtitle"
                            style={{
                              marginTop:
                                filteredComplaints.length > 0 ? "1.5rem" : "0",
                            }}
                          >
                            Suggestions ({filteredSuggestions.length})
                          </h4>
                          <div className="table-wrapper">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>Tracking ID</th>
                                  <th>Name</th>
                                  <th>District</th>
                                  <th>Category</th>
                                  <th>Subject</th>
                                  <th>Status</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredSuggestions.map((item, i) => (
                                  <tr
                                    key={item._id}
                                    onClick={() =>
                                      handleItemClick(item._id, "suggestion")
                                    }
                                  >
                                    <td>{i + 1}</td>
                                    <td>
                                      <span className="tracking-id">
                                        {item.trackingId}
                                      </span>
                                    </td>
                                    <td className="name-cell">
                                      {item.citizenName}
                                    </td>
                                    <td>{item.district}</td>
                                    <td>{item.category}</td>
                                    <td>{item.subject}</td>
                                    <td>
                                      <span
                                        className="status-badge"
                                        style={getStatusBadge(item.status)}
                                      >
                                        {item.status}
                                      </span>
                                    </td>
                                    <td>
                                      <button
                                        className="view-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleItemClick(
                                            item._id,
                                            "suggestion",
                                          );
                                        }}
                                      >
                                        View
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Recent Activities - Only 1 record */}
              <div className="recent-card-full">
                <h3 className="card-title">Recent Activity</h3>
                <div className="recent-list">
                  {complaints.length === 0 && suggestions.length === 0 ? (
                    <div className="empty-assigned">
                      <p>No recent activities</p>
                    </div>
                  ) : (
                    <>
                      {/* Show only the most recent complaint (first item) */}
                      {complaints.length > 0 && (
                        <div
                          key={complaints[0]._id}
                          className="recent-item"
                          onClick={() =>
                            handleItemClick(complaints[0]._id, "complaint")
                          }
                        >
                          <div className="recent-info">
                            <p className="recent-service">
                              Complaint: {complaints[0].subject}
                            </p>
                            <p className="recent-meta">
                              {complaints[0].citizenName} •{" "}
                              {complaints[0].district} •{" "}
                              {complaints[0].trackingId}
                            </p>
                          </div>
                          <div className="recent-actions">
                            <span
                              className="status-badge"
                              style={getStatusBadge(complaints[0].status)}
                            >
                              {complaints[0].status}
                            </span>
                            <button
                              className="recent-link"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleItemClick(complaints[0]._id, "complaint");
                              }}
                            >
                              View
                            </button>
                          </div>
                        </div>
                      )}
                      {/* If no complaints, show the most recent suggestion */}
                      {complaints.length === 0 && suggestions.length > 0 && (
                        <div
                          key={suggestions[0]._id}
                          className="recent-item"
                          onClick={() =>
                            handleItemClick(suggestions[0]._id, "suggestion")
                          }
                        >
                          <div className="recent-info">
                            <p className="recent-service">
                              Suggestion: {suggestions[0].subject}
                            </p>
                            <p className="recent-meta">
                              {suggestions[0].citizenName} •{" "}
                              {suggestions[0].district} •{" "}
                              {suggestions[0].trackingId}
                            </p>
                          </div>
                          <div className="recent-actions">
                            <span
                              className="status-badge"
                              style={getStatusBadge(suggestions[0].status)}
                            >
                              {suggestions[0].status}
                            </span>
                            <button
                              className="recent-link"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleItemClick(
                                  suggestions[0]._id,
                                  "suggestion",
                                );
                              }}
                            >
                              View
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ========== COMPLAINTS TAB ========== */}
          {activeTab === "complaints" && (
            <div className="table-management">
              <div className="management-header">
                <h2>Complaints</h2>
                <div className="header-actions">
                  <span className="item-count">Total: {complaints.length}</span>
                </div>
              </div>

              {complaints.length === 0 ? (
                <div className="empty-state">No complaints found</div>
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Tracking ID</th>
                        <th>Name</th>
                        <th>District</th>
                        <th>Category</th>
                        <th>Subject</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {complaints.map((complaint, index) => (
                        <tr
                          key={complaint._id}
                          onClick={() =>
                            handleItemClick(complaint._id, "complaint")
                          }
                        >
                          <td>{index + 1}</td>
                          <td>
                            <span className="tracking-id">
                              {complaint.trackingId}
                            </span>
                          </td>
                          <td className="name-cell">{complaint.citizenName}</td>
                          <td>{complaint.district}</td>
                          <td>{complaint.category}</td>
                          <td>{complaint.subject}</td>
                          <td>
                            <span
                              className="status-badge"
                              style={getStatusBadge(complaint.status)}
                            >
                              {complaint.status}
                            </span>
                          </td>
                          <td>
                            <button
                              className="view-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleItemClick(complaint._id, "complaint");
                              }}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ========== SUGGESTIONS TAB ========== */}
          {activeTab === "suggestions" && (
            <div className="table-management">
              <div className="management-header">
                <h2>Suggestions</h2>
                <div className="header-actions">
                  <span className="item-count">
                    Total: {suggestions.length}
                  </span>
                </div>
              </div>

              {suggestions.length === 0 ? (
                <div className="empty-state">No suggestions found</div>
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Tracking ID</th>
                        <th>Name</th>
                        <th>District</th>
                        <th>Category</th>
                        <th>Subject</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suggestions.map((suggestion, index) => (
                        <tr
                          key={suggestion._id}
                          onClick={() =>
                            handleItemClick(suggestion._id, "suggestion")
                          }
                        >
                          <td>{index + 1}</td>
                          <td>
                            <span className="tracking-id">
                              {suggestion.trackingId}
                            </span>
                          </td>
                          <td className="name-cell">
                            {suggestion.citizenName}
                          </td>
                          <td>{suggestion.district}</td>
                          <td>{suggestion.category}</td>
                          <td>{suggestion.subject}</td>
                          <td>
                            <span
                              className="status-badge"
                              style={getStatusBadge(suggestion.status)}
                            >
                              {suggestion.status}
                            </span>
                          </td>
                          <td>
                            <button
                              className="view-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleItemClick(suggestion._id, "suggestion");
                              }}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .minister-dashboard {
          min-height: 100vh;
          background: #f5f9ff;
        }

        .dashboard-page {
          min-height: 100vh;
          background: #f5f9ff;
        }

        .spinner-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: calc(100vh - 70px);
          width: 100%;
          background: #f5f9ff;
        }

        .spinner-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #e8edf5;
          border-top-color: #2563eb;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        .loading-text {
          color: #6b7a8f;
          font-size: 0.95rem;
          font-weight: 500;
          animation: pulse 1.5s ease-in-out infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }

        .error-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: calc(100vh - 70px);
          width: 100%;
          background: #f5f9ff;
        }

        .error-container {
          text-align: center;
          padding: 2rem;
          background: white;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
        }

        .error-text {
          color: #dc2626;
          margin-bottom: 1rem;
        }

        .retry-btn {
          padding: 0.6rem 1.5rem;
          background: #2563eb;
          color: white;
          border: none;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .retry-btn:hover {
          background: #1d4ed8;
          transform: translateY(-2px);
        }

        /* Layout */
        .dashboard-layout {
          display: flex;
          min-height: calc(100vh - 70px);
          max-width: 100%;
          margin: 0;
          padding: 0;
          background: #f5f9ff;
        }

        /* Sidebar */
        .dashboard-sidebar {
          width: 260px;
          min-width: 260px;
          background: white;
          border-right: 1px solid #e8edf5;
          padding: 1.5rem 0;
          display: flex;
          flex-direction: column;
          height: calc(100vh - 70px);
          position: sticky;
          top: 70px;
          overflow-y: auto;
        }

        .sidebar-header {
          padding: 0 1.5rem 1.5rem;
          border-bottom: 1px solid #e8edf5;
        }

        .sidebar-header h2 {
          font-size: 1.1rem;
          font-weight: 700;
          color: #1a2a3a;
          margin: 0;
        }

        .sidebar-user {
          font-size: 0.8rem;
          color: #6b7a8f;
          margin: 0.25rem 0 0;
        }

        .sidebar-nav {
          flex: 1;
          padding: 1rem 0.75rem;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .sidebar-tab {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.6rem 1rem;
          border: none;
          background: none;
          color: #6b7a8f;
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          width: 100%;
          text-align: left;
        }

        .sidebar-tab:hover {
          background: #f0f4ff;
          color: #1a2a3a;
        }

        .sidebar-tab.active {
          background: #01411c;
          color: white;
          font-weight: 600;
        }

        .sidebar-tab .tab-label {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .sidebar-footer {
          padding: 1rem 1.5rem;
          border-top: 1px solid #e8edf5;
          display: flex;
          justify-content: center;
        }

        .sidebar-footer .role-badge {
          display: inline-block;
          padding: 0.25rem 1.25rem;
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          background: #dbeafe;
          color: #1e40af;
        }

        /* Content */
        .dashboard-content {
          flex: 1;
          padding: 1.5rem 2rem;
          overflow-y: auto;
          max-height: calc(100vh - 70px);
        }

        .content-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .content-header h1 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1a2a3a;
          margin: 0;
        }

        .header-actions {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
          align-items: center;
        }

        .item-count {
          padding: 0.3rem 1rem;
          background: #dbeafe;
          color: #1e40af;
          font-size: 0.75rem;
          font-weight: 600;
        }

        /* Stats Grid - All 6 cards */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          padding: 1.25rem;
          text-align: center;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
          border: 2px solid transparent;
          transition: all 0.3s ease;
          cursor: pointer;
          position: relative;
          user-select: none;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(37, 99, 235, 0.08);
          border-color: #6dd4ff;
        }

        .stat-card.active-filter {
          border-color: #2563eb !important;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2);
          transform: translateY(-4px);
        }

        .stat-card.active-filter::after {
          content: "●";
          position: absolute;
          top: 8px;
          right: 10px;
          color: #2563eb;
          font-size: 10px;
        }

        .stat-number {
          font-size: 2rem;
          font-weight: 700;
          color: #1a2a3a;
        }

        .stat-label {
          color: #6b7a8f;
          font-size: 0.8rem;
          margin-top: 0.25rem;
          font-weight: 500;
        }

        .stat-sub {
          font-size: 0.6rem;
          color: #8a9ab0;
          margin-top: 0.15rem;
        }

        .stat-number.pending-number {
          color: #f39c12;
        }

        .stat-number.approved-number {
          color: #27ae60;
        }

        .stat-number.rejected-number {
          color: #e74c3c;
        }

        .stat-number.resolved-number {
          color: #2ecc71;
        }

        .pending-card {
          border-color: #f39c12;
        }

        .approved-card {
          border-color: #27ae60;
        }

        .rejected-card {
          border-color: #e74c3c;
        }

        .resolved-card {
          border-color: #2ecc71;
        }

        /* Filtered Section */
        .filtered-section {
          background: white;
          padding: 1.5rem 2rem;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
          border: 1px solid #e8edf5;
          margin-bottom: 2rem;
          animation: slideDown 0.3s ease;
        }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .filtered-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          padding-bottom: 0.75rem;
          border-bottom: 2px solid #e8edf5;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .filtered-subtitle {
          font-size: 0.95rem;
          font-weight: 600;
          color: #1a2a3a;
          margin: 0 0 0.75rem 0;
        }

        .clear-filter-btn {
          padding: 0.3rem 1rem;
          background: #fee2e2;
          color: #dc2626;
          border: none;
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .clear-filter-btn:hover {
          background: #fecaca;
        }

        /* Recent Cards */
        .recent-card-full {
          background: white;
          padding: 1.5rem 2rem;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
          border: 1px solid #e8edf5;
          width: 100%;
        }

        .card-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1a2a3a;
          margin: 0 0 1rem 0;
        }

        .recent-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .recent-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1rem;
          background: #f8faff;
          flex-wrap: wrap;
          gap: 0.5rem;
          border: 1px solid transparent;
          transition: all 0.2s;
          cursor: pointer;
        }

        .recent-item:hover {
          background: #f0f4ff;
          border-color: #dbeafe;
        }

        .recent-info {
          flex: 1;
          min-width: 200px;
        }

        .recent-service {
          font-weight: 600;
          color: #1a2a3a;
          font-size: 0.95rem;
        }

        .recent-meta {
          font-size: 0.8rem;
          color: #6b7a8f;
          margin-top: 0.15rem;
        }

        .recent-actions {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .recent-link {
          color: #2563eb;
          font-weight: 600;
          font-size: 0.85rem;
          text-decoration: none;
          padding: 0.25rem 0.5rem;
          background: none;
          border: none;
          cursor: pointer;
          transition: color 0.2s;
        }

        .recent-link:hover {
          color: #1d4ed8;
        }

        .status-badge {
          display: inline-block;
          padding: 0.2rem 0.8rem;
          font-size: 0.7rem;
          font-weight: 600;
          color: white;
        }

        .empty-assigned {
          text-align: center;
          color: #6b7a8f;
          padding: 2rem 0;
          font-size: 1rem;
        }

        /* Table Management */
        .table-management {
          background: white;
          padding: 1.5rem 2rem;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
          border: 1px solid #e8edf5;
        }

        .management-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .management-header h2 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1a2a3a;
          margin: 0;
        }

        .table-wrapper {
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }

        .data-table thead {
          background: #6dd4ff;
          border-bottom: 3px solid #4a9bc4;
        }

        .data-table thead th {
          padding: 0.75rem 1rem;
          text-align: left;
          font-weight: 700;
          color: white;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .data-table tbody tr {
          border-bottom: 1px solid #f0f4ff;
          transition: background 0.2s;
          cursor: pointer;
          text-align: justify;
        }

        .data-table tbody tr:hover {
          background: #f8faff;
        }

        .data-table tbody td {
          padding: 0.75rem 1rem;
          color: #1a2a3a;
        }

        .data-table .tracking-id {
          font-family: monospace;
          font-size: 0.7rem;
          background: #f0f4ff;
          padding: 0.15rem 0.5rem;
          color: #1a2a3a;
        }

        .data-table .name-cell {
          font-weight: 500;
          textalign: justify;
        }

        .data-table .status-badge {
          display: inline-block;
          padding: 0.2rem 0.8rem;
          font-size: 0.7rem;
          font-weight: 600;
          color: white;
        }

        .view-btn {
          padding: 0.25rem 1rem;
          background: #6dd4ff;
          color: white;
          border: none;
          font-weight: 600;
          font-size: 0.75rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .view-btn:hover {
          background: #4a9bc4;
          transform: scale(1.05);
          box-shadow: 0 2px 10px rgba(52, 197, 255, 0.3);
        }

        .empty-state {
          text-align: center;
          padding: 2rem;
          color: #6b7a8f;
          background: #f8faff;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(3, 1fr);
          }
          .sidebar-tab .tab-label {
            font-size: 0.75rem;
          }
        }

        @media (max-width: 768px) {
          .dashboard-layout {
            flex-direction: column;
          }
          .dashboard-sidebar {
            width: 100%;
            min-width: 100%;
            height: auto;
            position: relative;
            top: 0;
            border-right: none;
            border-bottom: 1px solid #e8edf5;
            padding: 0.75rem 0;
          }
          .sidebar-header {
            padding: 0 1rem 0.75rem;
          }
          .sidebar-nav {
            flex-direction: row;
            flex-wrap: wrap;
            padding: 0.5rem 0.75rem;
            gap: 0.25rem;
          }
          .sidebar-tab {
            flex: 1;
            min-width: 80px;
            justify-content: center;
            padding: 0.4rem 0.6rem;
            font-size: 0.75rem;
          }
          .sidebar-footer {
            display: none;
          }
          .dashboard-content {
            padding: 1rem;
          }
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .content-header {
            flex-direction: column;
            align-items: flex-start;
          }
          .data-table {
            font-size: 0.75rem;
          }
          .data-table th,
          .data-table td {
            padding: 0.5rem;
          }
          .data-table td .view-btn {
            font-size: 0.65rem;
            padding: 0.15rem 0.6rem;
          }
          .data-table .tracking-id {
            font-size: 0.6rem;
          }
          .data-table .status-badge {
            font-size: 0.6rem;
            padding: 0.1rem 0.5rem;
          }
        }

        @media (max-width: 640px) {
          .stats-grid {
            grid-template-columns: 1fr 1fr;
          }
          .stat-number {
            font-size: 1.5rem;
          }
          .dashboard-content {
            padding: 0.75rem;
          }
          .table-management,
          .recent-card-full,
          .filtered-section {
            padding: 1rem;
          }
          .data-table th,
          .data-table td {
            padding: 0.4rem 0.5rem;
            font-size: 0.7rem;
          }
          .data-table td .view-btn {
            font-size: 0.6rem;
            padding: 0.1rem 0.5rem;
          }
          .data-table .tracking-id {
            font-size: 0.55rem;
          }
          .data-table .status-badge {
            font-size: 0.55rem;
            padding: 0.1rem 0.4rem;
          }
          .recent-item {
            flex-direction: column;
            align-items: flex-start;
          }
          .recent-actions {
            width: 100%;
            justify-content: flex-start;
          }
        }
      `}</style>
    </div>
  );
};

export default MinisterDashboard;